import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageSourcePropType, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
	DuelChipStackPng,
	DuelGiftBoxPng,
	DuelRealWorldRewardPng,
	DuelXpJpg,
} from '../../../assets/duels';
import type { DuelPlayerRewardDto, DuelRewardType } from '../../../services/api';
import { selectIsLogged } from '../../../store/auth/authReducer';
import {
	duelRewardClaimErrorCleared,
	duelRewardClaimSubmit,
	duelsRewardsRequest,
} from '../../../store/duels/duelsActions';
import {
	isRewardInPlay,
	selectDuelClaimErrorKey,
	selectDuelClaimingRewardIds,
	selectDuelRewards,
	selectDuelRewardsErrorKey,
	selectDuelRewardsLoaded,
} from '../../../store/duels/duelsReducer';
import { RootStackParamList } from '../../navigator';
import { TouchableOpacity } from '../common/Touchable';
import { DuelRewardTile } from './DuelRedeemUi';
import { useSeenDuelRewards } from './duelRewardSeen';
import {
	DuelRewardStatusPill,
	formatShortDate,
	lastRequestedAt,
	rewardPillText,
	rewardTitle,
} from './DuelRewardUi';
import { duelText } from './duelsText';
import { formatScore } from './duelsTheme';
import { DuelButton, DuelLoading, DuelNotice, DuelPanel, duelTextStyles } from './DuelsUi';

type NavType = NativeStackNavigationProp<RootStackParamList>;

const HISTORY_PREVIEW = 3;
const COLUMNS = 3;
const TILE_GAP = 8;

const DIGITAL_ART: Record<DuelRewardType, ImageSourcePropType> = {
	Chips: DuelChipStackPng,
	Experience: DuelXpJpg,
	VipDays: DuelGiftBoxPng,
	RealWorld: DuelRealWorldRewardPng,
};

export const rewardLabel = (t: Parameters<typeof rewardTitle>[0], reward: DuelPlayerRewardDto) =>
	rewardTitle(t, reward) || formatScore(reward.value);

export const redemptionHistory = (rewards: DuelPlayerRewardDto[]) =>
	rewards
		.filter(
			reward =>
				reward.rewardType === 'RealWorld' &&
				reward.history.some(entry => entry.action === 'requested'),
		)
		.sort((a, b) =>
			(lastRequestedAt(b.history) ?? '').localeCompare(lastRequestedAt(a.history) ?? ''),
		);

type RowProps = {
	reward: DuelPlayerRewardDto;
	onPress: () => void;
	scale: number;
	withDate?: boolean;
};

export const DuelRedemptionRow = ({ reward, onPress, scale, withDate }: RowProps) => {
	const { t } = useTranslation();
	const meta = duelText(t, 'T_SITE_DUELS_REWARD_SEASON_LINE', [
		reward.seasonName,
		formatScore(reward.threshold),
	]);
	return (
		<TouchableOpacity
			onPress={onPress}
			accessibilityRole='button'
			style={[
				styles.historyRow,
				{
					padding: 12 * scale,
					columnGap: 12 * scale,
					borderRadius: 10 * scale,
					borderWidth: Math.max(1, 1 * scale),
				},
			]}
		>
			<Image
				source={DuelRealWorldRewardPng}
				style={{ width: 40 * scale, height: 40 * scale }}
				resizeMode='contain'
			/>
			<View style={[styles.flex, { rowGap: 4 * scale }]}>
				<Text style={[duelTextStyles.title, { fontSize: 15 * scale }]}>
					{rewardLabel(t, reward)}
				</Text>
				<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
					{withDate ? `${meta} · ${formatShortDate(lastRequestedAt(reward.history))}` : meta}
				</Text>
				<DuelRewardStatusPill
					status={reward.status}
					scale={scale}
					label={rewardPillText(t, reward.status)}
				/>
			</View>
			<Text style={[duelTextStyles.gold, { fontSize: 20 * scale }]}>›</Text>
		</TouchableOpacity>
	);
};

type Props = { width: number; scale: number };

export const DuelRewardsPanel = ({ width, scale }: Props) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigation = useNavigation<NavType>();
	const isLogged = useSelector(selectIsLogged);
	const loaded = useSelector(selectDuelRewardsLoaded);
	const errorKey = useSelector(selectDuelRewardsErrorKey);
	const rewards = useSelector(selectDuelRewards);
	const claimingIds = useSelector(selectDuelClaimingRewardIds);
	const claimErrorKey = useSelector(selectDuelClaimErrorKey);
	const seenIds = useSeenDuelRewards();

	useEffect(() => {
		if (isLogged) dispatch(duelsRewardsRequest());
	}, [dispatch, isLogged]);

	useEffect(() => {
		dispatch(duelRewardClaimErrorCleared());
	}, [dispatch]);

	const realWorldTiles = rewards.filter(
		reward => reward.rewardType === 'RealWorld' && isRewardInPlay(reward),
	);
	const claimTiles = rewards.filter(reward => reward.rewardType !== 'RealWorld' && reward.canClaim);
	const tiles = [...realWorldTiles, ...claimTiles];
	const history = redemptionHistory(rewards);
	const tileWidth = (width - TILE_GAP * scale * (COLUMNS - 1)) / COLUMNS;

	const leaveInventoryFor = (go: () => void) => {
		navigation.goBack();
		go();
	};
	const open = (reward: DuelPlayerRewardDto) =>
		leaveInventoryFor(() => navigation.navigate('DuelReward', { entitlementId: reward.id }));
	const openHistory = () => leaveInventoryFor(() => navigation.navigate('DuelRedemptionHistory'));

	if (!isLogged) return null;
	if (!loaded) {
		return errorKey ? (
			<DuelNotice
				scale={scale}
				width={width}
				text={duelText(t, errorKey)}
				actionLabel={duelText(t, 'T_SITE_DUELS_RETRY')}
				onAction={() => dispatch(duelsRewardsRequest())}
			/>
		) : (
			<DuelLoading />
		);
	}

	if (!tiles.length && !history.length) {
		return (
			<DuelNotice scale={scale} width={width} text={duelText(t, 'T_SITE_DUELS_REWARDS_EMPTY')} />
		);
	}

	const tileFooter = (reward: DuelPlayerRewardDto) => {
		if (reward.rewardType !== 'RealWorld') {
			return (
				<DuelButton
					label={duelText(t, 'T_SITE_DUELS_CLAIM')}
					onPress={() => dispatch(duelRewardClaimSubmit(reward.id))}
					width={tileWidth - 16 * scale}
					height={32}
					labelSize={10}
					scale={scale}
					busy={claimingIds.includes(reward.id)}
				/>
			);
		}
		if (reward.canRequest) {
			return (
				<DuelButton
					label={duelText(t, 'T_SITE_DUELS_REQUEST_NOW')}
					onPress={() => open(reward)}
					width={tileWidth - 16 * scale}
					height={32}
					labelSize={10}
					scale={scale}
				/>
			);
		}
		return (
			<View style={{ paddingBottom: 6 * scale }}>
				<DuelRewardStatusPill
					status={reward.status}
					scale={scale}
					label={rewardPillText(t, reward.status)}
				/>
			</View>
		);
	};

	return (
		<View style={{ width, rowGap: 12 * scale }}>
			{tiles.length ? (
				<View style={[styles.grid, { columnGap: TILE_GAP * scale, rowGap: TILE_GAP * scale }]}>
					{tiles.map(reward => (
						<DuelRewardTile
							key={reward.id}
							art={DIGITAL_ART[reward.rewardType as DuelRewardType] ?? DuelRealWorldRewardPng}
							title={rewardLabel(t, reward)}
							footer={tileFooter(reward)}
							newLabel={
								reward.canRequest && seenIds && !seenIds.includes(reward.id)
									? duelText(t, 'T_SITE_DUELS_NEW')
									: undefined
							}
							onPress={reward.rewardType === 'RealWorld' ? () => open(reward) : undefined}
							width={tileWidth}
							scale={scale}
						/>
					))}
				</View>
			) : null}

			{claimErrorKey ? (
				<Text style={[styles.error, { fontSize: 12 * scale }]}>{duelText(t, claimErrorKey)}</Text>
			) : null}

			{history.length ? (
				<DuelPanel scale={scale} width={width} style={{ padding: 9 * scale }}>
					<View style={styles.heading}>
						<Text style={[duelTextStyles.gold, { fontSize: 18 * scale }]}>
							{duelText(t, 'T_SITE_DUELS_REDEMPTION_HISTORY')}
						</Text>
						<TouchableOpacity onPress={openHistory} accessibilityRole='link' hitSlop={8}>
							<Text style={[duelTextStyles.title, { fontSize: 14 * scale }]}>
								{`${duelText(t, 'T_SITE_DUELS_SEE_ALL')} ›`}
							</Text>
						</TouchableOpacity>
					</View>
					{history.slice(0, HISTORY_PREVIEW).map(reward => (
						<DuelRedemptionRow
							key={reward.id}
							reward={reward}
							onPress={() => open(reward)}
							scale={scale}
						/>
					))}
				</DuelPanel>
			) : null}
		</View>
	);
};

const styles = StyleSheet.create({
	grid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
	},
	heading: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	historyRow: {
		flexDirection: 'row',
		alignItems: 'center',
		borderColor: 'rgba(224,135,18,0.45)',
		backgroundColor: 'rgba(0,0,0,0.25)',
	},
	flex: {
		flex: 1,
		minWidth: 0,
	},
	error: {
		color: '#FF8B77',
		fontWeight: '600',
		textAlign: 'center',
	},
});
